import type { SessionProfile } from '@/types';
import { portalLoginApi } from '@/features/portal-login/portalLoginApi';

let sessionPromise: Promise<SessionProfile | null> | null = null;

export async function getSession(): Promise<SessionProfile | null> {
  if (sessionPromise) {
    return sessionPromise;
  }

  sessionPromise = fetchSession();
  const session = await sessionPromise;
  sessionPromise = null;
  return session;
}

async function fetchSession(): Promise<SessionProfile | null> {
  try {
    const response = await fetch('/api/v1/auth/session', {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-ID': generateRequestId()
      }
    });

    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
      // eslint-disable-next-line no-console
      console.debug('[session] /auth/session status', response.status);
    }

    if (response.status === 401) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Session fetch failed: ${response.status}`);
    }

    const sessionData = (await response.json()) as SessionProfile;
    if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
      // eslint-disable-next-line no-console
      console.debug('[session] /auth/session payload', sessionData);
    }
    const session: SessionProfile = {
      ...sessionData,
      userId: sessionData.userId ?? sessionData.id,
      roles: sessionData.roles ?? []
    };

    return session;
  } catch (error) {
    console.error('Failed to fetch session:', error);
    return null;
  }
}

export function clearSession(): void {
  sessionPromise = null;
}

interface LoginOptions {
  register?: boolean;
}

export function login(returnUrl?: string, options?: LoginOptions): void {
  const params = new URLSearchParams();
  if (returnUrl) {
    const returnPath = normalizeLoginReturnPath(returnUrl);
    if (returnPath) {
      params.set('return_url', returnPath);
    }
  }
  if (options?.register) {
    params.set('register', 'true');
  }

  const query = params.toString();
  window.location.href = query ? `/api/v1/auth/login?${query}` : '/api/v1/auth/login';
}

export async function reauthenticate(returnUrl: string): Promise<void> {
  const returnPath = normalizeLoginReturnPath(returnUrl);
  if (!returnPath) throw new Error('Invalid login return path.');
  const selectors = new URL(returnPath, window.location.origin).searchParams.getAll('tenant');
  const options = await portalLoginApi.resolveOptions(selectors);
  if (!options.available) throw new Error('Login is currently unavailable.');
  const { redirectUrl } = await portalLoginApi.start({
    source: options.primary.source,
    tenantSlug: options.tenant?.slug,
    returnPath,
  });
  window.location.assign(redirectUrl);
}

function normalizeLoginReturnPath(returnUrl: string): string | null {
  try {
    const parsed = new URL(returnUrl, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

export async function logout(): Promise<void> {
  window.location.assign('/signing-out');
}

export function redirectToLogout(): void {
  const returnUrl = `${resolvePublicAppOrigin()}/?signed_out=1`;
  const params = new URLSearchParams({ return_url: returnUrl });
  window.location.assign(`/api/v1/auth/logout?${params.toString()}`);
}

function generateRequestId(): string {
  return `req_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function resolvePublicAppOrigin(): string {
  const configuredOrigin = import.meta.env.VITE_PUBLIC_APP_ORIGIN?.trim();
  if (configuredOrigin) {
    return configuredOrigin.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}
